/**
 * Display labels for the regulatory-mandate model. Mirrors backend/src/modules/insights/mandates.ts
 * (a backend parity test fails if the two drift).
 */
export const ORG_SECTORS = ["financial", "telecom", "healthcare", "energy", "government"] as const;
export const DESIGNATIONS = ["critical_infrastructure", "vendors_to_government", "national_security", "securities_market", "banking"] as const;

export const SECTOR_LABEL: Record<string, string> = {
  all: "All organisations",
  government: "Government",
  national_security: "National security systems",
  critical_infrastructure: "Critical infrastructure",
  financial: "Financial services",
  telecom: "Telecommunications",
  healthcare: "Healthcare",
  energy: "Energy",
  vendors_to_government: "Suppliers to government",
  securities_market: "Securities-market regulated entity",
  banking: "Bank, NBFC or payment-system operator",
};

export const JURISDICTION_LABEL: Record<string, string> = {
  IN: "India", US: "United States", "US-NSS": "US national security systems", EU: "European Union", UK: "United Kingdom",
  CA: "Canada", AU: "Australia", SG: "Singapore", HK: "Hong Kong", JP: "Japan", KR: "South Korea", AE: "United Arab Emirates",
  SA: "Saudi Arabia", DE: "Germany", FR: "France", CH: "Switzerland", IL: "Israel", NL: "Netherlands", CN: "China",
  INTL: "International",
};

/**
 * Industries offered when creating a company, with the regulated sector the engine infers from each
 * (backend sectorsForIndustry; "none" = no sector-specific instruments). Parity-tested in the backend.
 */
export const INDUSTRY_SECTOR: Record<string, string> = {
  "Banking": "financial",
  "Insurance": "financial",
  "Capital markets": "financial",
  "Payments and fintech": "financial",
  "Asset management": "financial",
  "Telecommunications": "telecom",
  "Healthcare": "healthcare",
  "Pharmaceuticals": "healthcare",
  "Energy and utilities": "energy",
  "Oil and gas": "energy",
  "Government": "government",
  "Public sector": "government",
  "Defence": "national_security",
  "Technology": "none",
  "Manufacturing": "none",
  "Retail": "none",
  "Transport and logistics": "none",
  "Education": "none",
  "Media": "none",
};

export const jurisdictionLabel = (j: string) => JURISDICTION_LABEL[j] ?? j;
export const sectorLabel = (s: string) => SECTOR_LABEL[s] ?? s.replace(/_/g, " ");

/** What the chosen industry means for regulatory scope (shown under the field). */
export function industryHint(industry: string): string {
  const t = industry.trim();
  if (!t) return 'Sets which sector regulators\' quantum-safe deadlines apply to this company.';
  const known = Object.entries(INDUSTRY_SECTOR).find(([k]) => k.toLowerCase() === t.toLowerCase());
  if (!known) return 'Not a listed industry: the regulated sector is inferred from the words used. Check it in Settings → Reporting profile.';
  return known[1] === 'none'
    ? 'No sector-specific regulator: only instruments for all organisations apply.'
    : `Regulated sector: ${sectorLabel(known[1])}.`;
}
